import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Activity, TrendingUp, Clock, XCircle, RefreshCw } from 'lucide-react';
import { useCTraderSignals } from '@/hooks/use-ctrader-signals';
import { CTraderSignalFilters } from './CTraderSignalFilters';
import { CTraderSignalTable } from './CTraderSignalTable';
import { CTraderSignalDetailModal } from './CTraderSignalDetailModal';
import { SignalAccountStatus } from '@/types/signal';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

export const CTraderSignalManagementPage = () => {
  const [selectedSignal, setSelectedSignal] = useState<SignalAccountStatus | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const {
    signals,
    total,
    page,
    pageSize,
    filters,
    stats,
    isLoading,
    isStatsLoading,
    handlePageChange,
    handlePageSizeChange,
    handleFiltersChange,
    refetch
  } = useCTraderSignals();

  const handleViewDetails = (signal: SignalAccountStatus) => {
    setSelectedSignal(signal);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setSelectedSignal(null);
    setIsDetailModalOpen(false);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Signal CTrader Management</h1>
          <p className="text-muted-foreground">
            Monitor and manage CTrader signal execution status
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Signals</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isStatsLoading ? '...' : stats?.total || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-500">
              {isStatsLoading ? '...' : stats?.open || 0}
            </div>
            {stats && stats.total > 0 && (
              <p className="text-xs text-muted-foreground">
                {((stats.open / stats.total) * 100).toFixed(1)}% success rate
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Processing</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">
              {isStatsLoading ? '...' : stats?.process || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {isStatsLoading ? '...' : stats?.rejected || 0}
            </div>
            {stats && stats.total > 0 && (
              <p className="text-xs text-muted-foreground">
                {((stats.rejected / stats.total) * 100).toFixed(1)}% error rate
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <CTraderSignalFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onRefresh={refetch}
        isLoading={isLoading}
      />

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Signal Status List</CardTitle>
          <p className="text-sm text-muted-foreground">
            Showing {signals.length} of {total} signals
          </p>
        </CardHeader>
        <CardContent>
          <CTraderSignalTable
            signals={signals}
            isLoading={isLoading}
            onViewDetails={handleViewDetails}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({total} total signals)
              </div>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => page > 1 && handlePageChange(page - 1)}
                      className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          onClick={() => handlePageChange(pageNum)}
                          isActive={page === pageNum}
                          className="cursor-pointer"
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => page < totalPages && handlePageChange(page + 1)}
                      className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <CTraderSignalDetailModal
        signal={selectedSignal}
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
      />
    </div>
  );
};